/**
 * =============================================================================
 * SPEQ55 RFP Pro - 404 Not Found Page
 * =============================================================================
 *
 * This page is displayed when a user navigates to a route that doesn't exist.
 * It provides:
 * 1. Clear indication that the page wasn't found
 * 2. Helpful suggestions for what to do next
 * 3. Navigation back to the dashboard or home
 *
 * Design Considerations:
 * ----------------------
 * - Friendly, non-technical messaging
 * - Clear call-to-action buttons
 * - Consistent branding
 * - Accessibility compliant
 *
 * @module pages/NotFound
 * @author SPEQ55
 */

import { Link } from 'react-router-dom';

// =============================================================================
// NOT FOUND COMPONENT
// =============================================================================

/**
 * 404 Not Found page component.
 * Displayed when no route matches the current URL.
 *
 * @returns {JSX.Element} The rendered 404 page
 */
function NotFound() {
  return (
    <div className="min-h-screen bg-accent-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center">
        {/* Illustration / Icon */}
        <div className="mb-8">
          <div className="w-32 h-32 mx-auto bg-gradient-to-br from-primary-100 to-secondary-100 rounded-full flex items-center justify-center">
            <span className="text-6xl">🔍</span>
          </div>
        </div>

        {/* Error Code */}
        <h1 className="text-7xl font-bold text-gradient">404</h1>

        {/* Error Message */}
        <h2 className="mt-4 text-2xl font-semibold text-accent-800">Page Not Found</h2>

        <p className="mt-4 text-accent-600">
          Oops! The page you&apos;re looking for doesn&apos;t exist or has been moved. Let&apos;s
          get you back on track.
        </p>

        {/* Action Buttons */}
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link to="/" className="btn-primary">
            Go to Home
          </Link>
          <button onClick={() => window.history.back()} className="btn-outline">
            Go Back
          </button>
        </div>

        {/* Help Links */}
        <div className="mt-12 pt-8 border-t border-accent-200">
          <p className="text-sm text-accent-500 mb-4">Here are some helpful links:</p>
          <div className="flex flex-wrap justify-center gap-4 text-sm">
            <Link to="/" className="text-primary-600 hover:text-primary-700">
              Home
            </Link>
            <span className="text-accent-300">•</span>
            <Link to="/about" className="text-primary-600 hover:text-primary-700">
              About
            </Link>
            <span className="text-accent-300">•</span>
            <Link to="/pricing" className="text-primary-600 hover:text-primary-700">
              Pricing
            </Link>
            <span className="text-accent-300">•</span>
            <Link to="/login" className="text-primary-600 hover:text-primary-700">
              Sign In
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default NotFound;
